import { useMemo, useState } from "react"
// import useModelsStore from "@/stores/models"
import styles from "./ListRetailItem.module.scss"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBagShopping, faMemoCircleInfo } from "@fortawesome/pro-regular-svg-icons"
import { faStars } from "@fortawesome/pro-solid-svg-icons"
import useTranslation from "@/Hooks/useTranslation"
import useHelpers from "@/Hooks/useHelpers"
import useTarifs from "@/Hooks/useTarifs"
import useRetailCollections from "@/Hooks/useRetailCollections"
import useSales from "@/Hooks/useSales"
import DynImage from "../DynImage"
import usePrefStore from "@/stores/prefs"
import useRetailRoleForCustomer from "@/Hooks/useRetailRoleForCustomer"
import glob_const from "@/libs/glob_const"
// import useTest from "@/Hooks/useTest"

function ListRetailItem(props) {
   const { curCustomer, retailRole, isRetailManager } = usePrefStore()
   // PCB (Par combien) visible pour admin/N1 du client courant uniquement.
   const roleForCustomer = useRetailRoleForCustomer(curCustomer)
   const canShowPCB = roleForCustomer.isAdmin || roleForCustomer.isN1
   // Panier désactivé pour les commerciaux : ni manager (N1/N2), ni admin, ni user-client
   // (ni simulation debug RET/ERE/INT de la vue client). Cf. traitement HJO (ListItem).
   const isCommercial =
      !isRetailManager &&
      retailRole !== 'admin' &&
      retailRole !== 'client' &&
      retailRole !== 'RET' && retailRole !== 'ERE' && retailRole !== 'INT'
   const { trans, lang } = useTranslation()
   const { formatLengthWithNC, formatRetailWeightWithNC, formatNumberWithNC, formatCESPrice, formatRetailPrice } = useHelpers()
   const { getMinMaxCessPriceForModel } = useTarifs()
   const { getCustomerById } = useSales()
   const customer = useMemo(() => curCustomer ? getCustomerById(curCustomer) : null, [curCustomer, getCustomerById])
   const [showArgu, setShowArgu] = useState(false)
   const { getRetailModelById, getRetailTypologieById, getRetailGammeById, getRetailCollectionById, getRetailCycleById, isExcluBoutique } = useRetailCollections()

   // useMemo plutôt que useState/useEffect : évite la boucle quand getRetailModelById/getRetailXxxById
   // sont recréés et que destructModel renvoie un nouvel objet à chaque appel.
   const model = useMemo(() => getRetailModelById(props.id), [getRetailModelById, props.id])
   // (gamme retiré : non utilisé dans le rendu)
   const collection = useMemo(() => model ? getRetailCollectionById(model.collec).label : '', [model, getRetailCollectionById])
   const typo = useMemo(() => model ? getRetailTypologieById(model.typo as string).label : '', [model, getRetailTypologieById])
   const cycle = useMemo(() => model ? getRetailCycleById(model.cycle).label : '', [model, getRetailCycleById])
   const argu = useMemo(() => isExcluBoutique(model), [model, isExcluBoutique])

   // gamme calculé à la demande (non rendu mais conservé au cas où)
   useMemo(() => model ? getRetailGammeById(model.gamme).label : '', [model, getRetailGammeById])

   const minMaxCession = useMemo(() => {
      if (!model) return { min: 0, max: 0 }
      const minmax = getMinMaxCessPriceForModel(model.id)
      return minmax.reason ? { min: 0, max: 0 } : minmax
   }, [getMinMaxCessPriceForModel, model])

   function showQtyPicker() {
      if (props.onShowQtyPicker) {
         props.onShowQtyPicker(props.id)
      }
   }

   // Texte de la "fiche article" : description longue (model.descr[lang]), produite par
   // +get-retail-modeles-texte.ts côté back (concat multi-lignes du WS Referentiel_Modele_Retail_Texte).
   // Fallback sur l'autre langue si la langue courante est vide.
   const descrText = useMemo(() => {
      const d = (model as any)?.descr as { fr?: string | null, en?: string | null } | undefined
      if (!d) return null
      const primary = lang === 'en' ? d.en : d.fr
      const secondary = lang === 'en' ? d.fr : d.en
      const t = (typeof primary === 'string' && primary.trim()) ? primary : (typeof secondary === 'string' && secondary.trim()) ? secondary : null
      return t ? t.trim() : null
   }, [model, lang])

   function toggleArgu() {
      if (descrText) setShowArgu(prev => !prev)
   }

   const modelImage = useMemo(() => {
      if (!model || !model.id) return null
      return <DynImage className={styles.imgmodel} src={[`catalogue/ecrins/${model.id}.jpg`, model.bymessika ? model.bymessika[0] : null]} alt={`Item ${model.id}`} />
   }, [model])

   return (model &&
      <div className={styles.listitem}>
         {/* <img src={`catalogue/modeles/${model.id}@2x.png`} alt={`Model ${model.id}`} /> */}
         {modelImage}
         <div className={styles.infobox}>
            {/* <span className={styles.gamme_family}>{gamme}/{collection}</span> */}
            <span className={styles.gamme_family}>{collection}</span>
            <span className={styles.label}>
               {props.id} - {model.label || ''}
               {argu && <span className={styles.argu}><FontAwesomeIcon icon={faStars} /></span>}
               {cycle && <span className={styles.cycle}>{cycle}</span>}
            </span>
            <span className={styles.subfamily}>
               {model.desig || ''}
            </span>
            {typo && <span className={styles.porte}>{typo}</span>}
            <table className={styles.infotable}>
               <thead>
                  <tr>
                     <th>{trans("?Largeur")}</th>
                     <th>{trans("?Longueur")}</th>
                     <th>{trans("?Hauteur")}</th>
                     <th>{trans("?Poids brut")}</th>
                     <th>{trans("?Poids net")}</th>
                     {canShowPCB && model.fam === glob_const.retail_fam_pcb && <th>{trans("?Par combien")}</th>}
                  </tr>
               </thead>
               <tbody>
                  <tr>
                     <td>{`${formatLengthWithNC(model.larg)}`}</td>
                     <td>{`${formatLengthWithNC(model.long)}`}</td>
                     <td>{`${formatLengthWithNC(model.haut)}`}</td>
                     <td>{`${formatRetailWeightWithNC(model.pds_brut)}`}</td>
                     <td>{`${formatRetailWeightWithNC(model.pds_net)}`}</td>
                     {canShowPCB && model.fam === glob_const.retail_fam_pcb && <td>
                        FR{lang === "fr" ? ' ' : ''}:{`${model.pcb_fr ? formatNumberWithNC(model.pcb_fr) : trans("?NC")}`}, US{lang === "fr" ? ' ' : ''}:{`${model.pcb_us ? formatNumberWithNC(model.pcb_us) : trans("?NC")}`}, CN{lang === "fr" ? ' ' : ''}:{`${model.pcb_cn ? formatNumberWithNC(model.pcb_cn) : trans("?NC")}`}, JP{lang === "fr" ? ' ' : ''}:{`${model.pcb_jp ? formatNumberWithNC(model.pcb_jp) : trans("?NC")}`}
                     </td>}
                     <td>{model.ecrin}</td>
                  </tr>
               </tbody>
            </table>
            {showArgu && descrText && (
               <div className={styles.argu_panel}>{descrText}</div>
            )}
         </div>
         <div className={styles.pricebox}>
            <span className={styles.price}>{formatRetailPrice(model, formatCESPrice(minMaxCession.min), customer)}</span>
            <div className={styles.buttons}>
               <button onClick={toggleArgu} disabled={!descrText} className={!descrText ? styles.disabled : ''}>
                  <FontAwesomeIcon icon={faMemoCircleInfo} />
               </button>
               <button onClick={showQtyPicker} disabled={isCommercial} className={isCommercial ? styles.disabled : ''}><FontAwesomeIcon icon={faBagShopping} /></button>
            </div>
         </div>
      </div>
   )
}

export default ListRetailItem
